import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import PageHeader from '../components/PageHeader';
import CardShell from '../components/CardShell';
import './AdjustmentsPage.css';
import {
  listAdjustments, createAdjustment, updateAdjustment, deleteAdjustment,
  createAdjustmentLine, updateAdjustmentLine,
  listLines,
} from '../api/financials_pl';
import { listBsLines } from '../api/financials_bs';

function fmtAbs(v) {
  const n = Math.abs(Number(v || 0));
  if (n === 0) return '–';
  return n.toLocaleString('de-CH');
}
function fmtK(v) {
  const k = Math.round(Number(v) / 1000);
  return (k >= 0 ? '+' : '') + k.toLocaleString('de-CH');
}

export default function AdjustmentsPage({ activeTenant, addToast, year, embedded, onReady }) {
  const { t } = useTranslation();
  const p = useP();
  const [adjs, setAdjs]     = useState([]);
  const [lines, setLines]   = useState([]);
  const [bsLines, setBsLines] = useState([]);
  // Sélection multiple pour supprimer d'un coup (demande Olivier 2026-08-17).
  // Clé = l'écriture : ses pattes partent ensemble, comme avec la croix.
  const [sel, setSel] = useState(() => new Set());

  function reload() {
    if (!activeTenant) { if (onReady) onReady(); return; }
    Promise.all([
      listAdjustments(activeTenant.id, year).then(r => setAdjs(r.data || [])),
      listLines(activeTenant.id).then(r => setLines(r.data || [])),
      listBsLines(activeTenant.id).then(r => setBsLines(r.data || [])),
    ]).finally(() => { if (onReady) onReady(); });
  }

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeTenant, year]);

  async function handleCreate() {
    const month = new Date().getMonth() + 1;
    const adjRes = await createAdjustment(activeTenant.id, { year, month, description: null });
    if (!adjRes.data) { addToast(adjRes.error || p('Erreur création'), 'error'); return; }
    const lineRes = await createAdjustmentLine(activeTenant.id, adjRes.data.id, {
      line_id_a: null, line_id_b: null, amount: 0,
    });
    if (lineRes.data) reload();
    else addToast(lineRes.error || p('Erreur création ligne'), 'error');
  }

  async function handleDelete(adjId) {
    if (!window.confirm(p('Supprimer cette écriture ?'))) return;
    const res = await deleteAdjustment(activeTenant.id, adjId);
    setSel((s0) => { const n = new Set(s0); n.delete(adjId); return n; });
    if (res.data) reload();
    else addToast(res.error || p('Erreur'), 'error');
  }

  function toggleSel(adjId) {
    setSel((s0) => { const n = new Set(s0); if (n.has(adjId)) n.delete(adjId); else n.add(adjId); return n; });
  }

  async function handleDeleteMany(ids) {
    if (!ids.length) return;
    if (!window.confirm(`${p('Supprimer les écritures sélectionnées ?')} (${ids.length})`)) return;
    let ko = 0;
    for (const id of ids) {
      const res = await deleteAdjustment(activeTenant.id, id);
      if (!res.data) ko += 1;
    }
    setSel(new Set());
    if (ko) addToast(`${ko} ${p('écritures n’ont pas pu être supprimées')}`, 'error');
    reload();
  }

  async function handleUpdateAdj(adjId, field, value) {
    const res = await updateAdjustment(activeTenant.id, adjId, { [field]: value });
    if (res.data) reload();
    else addToast(res.error || p('Erreur'), 'error');
  }

  async function handleUpdateLine(adjId, lineId, field, value) {
    const res = await updateAdjustmentLine(activeTenant.id, adjId, lineId,
      { [field]: field === 'amount' ? Number(value) : (value || null) });
    if (res.data) reload();
    else addToast(res.error || p('Erreur'), 'error');
  }

  // Patte de/à : token "pl:<id>", "bs:<id>" ou "". On écrit les deux champs
  // (P&L et bilan) ensemble — la chaîne vide efface celui qui n'est pas choisi.
  async function handleUpdateLeg(adjId, lineId, side, token) {
    const plId = token.startsWith('pl:') ? token.slice(3) : '';
    const bsId = token.startsWith('bs:') ? token.slice(3) : '';
    const res = await updateAdjustmentLine(activeTenant.id, adjId, lineId, {
      [`line_id_${side}`]: plId,
      [`bs_line_id_${side}`]: bsId,
    });
    if (res.data) reload();
    else addToast(res.error || p('Erreur'), 'error');
  }

  if (!activeTenant) {
    return <main className="page-table-layout"><div className="page-table-empty">{p('Sélectionne une entreprise.')}</div></main>;
  }

  // Aplatir : une rangée par (adj, line). Si pas de ligne encore → rangée vide.
  const rows = adjs.flatMap(adj =>
    adj.lines.length > 0
      ? adj.lines.map(line => ({ adj, line }))
      : [{ adj, line: null }]
  );

  // Impact net P&L sur l'ensemble du tableau
  const netImpact = rows.reduce((sum, { line }) => {
    if (!line) return sum;
    return sum + (line.line_id_b ? Number(line.amount) : 0) - (line.line_id_a ? Number(line.amount) : 0);
  }, 0);
  const balanced = Math.abs(netImpact) < 0.01;

  return (
    <main className="page-table-layout">
      {!embedded && <PageHeader title={p('Ajustements & Corrections')} />}
      {/* Entête standard de carte (2026-09-01) : les actions vivaient dans le
          PageHeader, au-dessus du tableau qu'elles commandent. */}
      <CardShell
        className="report-card"
        title={p('Ajustements & Corrections')}
        primaryAction={(
          <>
            {sel.size > 0 && (
              <button className="p-btn adj-delsel" onClick={() => handleDeleteMany([...sel])}>
                {p('Supprimer la sélection')} ({sel.size})
              </button>
            )}
            <button className="p-btn p-btn--primary" onClick={handleCreate}>{p('+ Nouvelle écriture')}</button>
          </>
        )}
      >
      {rows.length === 0 && (
        <p className="page-table-empty">{t('phrases:p_395861a0ef', { year })}</p>
      )}

      {rows.length > 0 && (
        <div className="adj-table-wrap">
          <table className="adj-flat-table">
            <thead>
              <tr>
                <th className="adj-col--sel">
                  <input
                    type="checkbox"
                    aria-label={p('Tout sélectionner')}
                    checked={sel.size > 0 && sel.size === new Set(rows.map((r) => r.adj.id)).size}
                    onChange={(e) => setSel(e.target.checked ? new Set(rows.map((r) => r.adj.id)) : new Set())}
                  />
                </th>
                <th className="adj-col--period">{t('phrases:p_de2110b7a0')}</th>
                <th className="adj-col--label">{t('phrases:p_f5485bbae8')}</th>
                <th className="adj-col--amount">{p('Valeur')}</th>
                <th className="adj-col--line">{p('Compte (de)')}</th>
                <th className="adj-col--line">{p('Compte (à)')}</th>
                <th className="adj-col--memo">{p('Commentaire')}</th>
                <th className="adj-col--del"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ adj, line }) => (
                <AdjFlatRow
                  key={`${adj.id}-${line?.id ?? 'empty'}`}
                  adj={adj}
                  line={line}
                  year={year}
                  plLines={lines}
                  bsLines={bsLines}
                  onUpdateAdj={handleUpdateAdj}
                  onUpdateLine={handleUpdateLine}
                  onUpdateLeg={handleUpdateLeg}
                  onDelete={handleDelete}
                  selected={sel.has(adj.id)}
                  onToggleSel={() => toggleSel(adj.id)}
                />
              ))}
            </tbody>
            <tfoot>
              <tr className="adj-tfoot">
                <td colSpan={3}></td>
                <td className="adj-col--amount">
                  <span className={`adj-balance${balanced ? ' is-zero' : ' is-nonzero'}`}>
                    {balanced ? p('= 0 ✓') : `${fmtK(netImpact)} kCHF`}
                  </span>
                </td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      </CardShell>
    </main>
  );
}

function AdjFlatRow({ adj, line, year, plLines, bsLines, onUpdateAdj, onUpdateLine, onUpdateLeg, onDelete, selected, onToggleSel }) {
  const { t } = useTranslation();
  const p = useP();
  function updAdj(field, value) { onUpdateAdj(adj.id, field, value); }
  function updLine(field, value) { if (line) onUpdateLine(adj.id, line.id, field, value); }
  function updLeg(side, token)   { if (line) onUpdateLeg(adj.id, line.id, side, token); }

  return (
    <tr className={`adj-flat-row${selected ? ' is-sel' : ''}`}>
      <td className="adj-col--sel">
        <input type="checkbox" checked={!!selected} onChange={onToggleSel}
          aria-label={p('Sélectionner cette écriture')} />
      </td>
      <td className="adj-col--period">
        <span className="adj-ref">{adj.entry_ref}</span>
        <select
          className="adj-select adj-select--sm"
          value={adj.month}
          onChange={e => updAdj('month', Number(e.target.value))}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>{t(`common:months.${i + 1}`)} {year}</option>
          ))}
        </select>
      </td>
      <td>
        <DescCell value={adj.description} onSave={v => updAdj('description', v)} />
      </td>
      <td className="adj-col--amount">
        {line
          ? <AmountCell value={line.amount} onSave={v => updLine('amount', v)} />
          : <span className="adj-amount is-zero">–</span>}
      </td>
      <td>
        {line
          ? <LegSelect plValue={line.line_id_a} bsValue={line.bs_line_id_a}
                       onChange={val => updLeg('a', val)} plLines={plLines} bsLines={bsLines} />
          : '–'}
      </td>
      <td>
        {line
          ? <LegSelect plValue={line.line_id_b} bsValue={line.bs_line_id_b}
                       onChange={val => updLeg('b', val)} plLines={plLines} bsLines={bsLines} />
          : '–'}
      </td>
      <td>
        {line
          ? (
            <input
              className="adj-input adj-input--memo"
              defaultValue={line.memo || ''}
              placeholder={p('Commentaire…')}
              onBlur={e => {
                if (e.target.value !== (line.memo || '')) updLine('memo', e.target.value);
              }}
            />
          )
          : '–'}
      </td>
      <td>
        <button className="adj-del-btn" onClick={() => onDelete(adj.id)} title={p('Supprimer')}>×</button>
      </td>
    </tr>
  );
}

function LegSelect({ plValue, bsValue, onChange, plLines, bsLines }) {
  const { t } = useTranslation();
  const p = useP();
  const token = bsValue ? `bs:${bsValue}` : (plValue ? `pl:${plValue}` : '');
  const grouped = ls => {
    const g = {};
    for (const l of ls) {
      const sec = l.section_label || l.section;
      (g[sec] = g[sec] || []).push(l);
    }
    return g;
  };
  const plGroups = grouped(plLines);
  const bsGroups = grouped(bsLines);
  return (
    <select
      className="adj-select adj-select--line"
      value={token}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{p('— aucune / libre —')}</option>
      {Object.entries(plGroups).map(([sec, ls]) => (
        <optgroup key={`pl-${sec}`} label={t('phrases:p_4cbfd12321', { sec })}>
          {ls.map(l => <option key={l.id} value={`pl:${l.id}`}>{l.label}</option>)}
        </optgroup>
      ))}
      {Object.entries(bsGroups).map(([sec, ls]) => (
        <optgroup key={`bs-${sec}`} label={t('phrases:p_6ad0a93de1', { sec })}>
          {ls.map(l => <option key={l.id} value={`bs:${l.id}`}>{l.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function DescCell({ value, onSave }) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');

  function start() { setDraft(value || ''); setEditing(true); }
  function commit() {
    setEditing(false);
    if (draft !== (value || '')) onSave(draft.trim() || null);
  }

  if (editing) {
    return (
      <input
        className="adj-input"
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      />
    );
  }

  return (
    <span
      className={`adj-desc${value ? '' : ' adj-desc--empty'}`}
      onClick={start}
    >
      {value || p('Cliquer pour décrire…')}
    </span>
  );
}

function AmountCell({ value, onSave }) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');

  function start() { setDraft(value ? String(value) : ''); setEditing(true); }
  function commit() {
    setEditing(false);
    const v = parseFloat(String(draft).replace(/[\s']/g, '').replace(',', '.')) || 0;
    if (v !== Number(value)) onSave(v);
  }

  if (editing) {
    return (
      <input
        className="adj-amount-input"
        autoFocus
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  }

  const n = Number(value || 0);
  return (
    <span
      className={`adj-amount${n > 0 ? ' is-pos' : ' is-zero'}`}
      onClick={start}
      title={p('Cliquer pour modifier')}
    >
      {fmtAbs(n)}
    </span>
  );
}
